import React, {useEffect} from 'react';

import './_user-submitted-photos-modal.scss';
import SwiperWrapperContainer from '../../../../containers/Design/SwiperWrapperContainer';
import {translate} from '../../../../services';
import {CommentObject} from '../../../../shapes/comments';
import {Locale} from '../../../../shapes/locale';
import {CommentPhotoData, ReviewPhotoData} from '../../../../shapes/uploadedPhotos';
import {UserType} from '../../../../shapes/user';
import {ViewportType} from '../../../../shapes/viewport';
import getTimeElapsedSinceCreation from '../../../../utils/getTimeElapsedSinceCreation';
import {isNotUndefined, objectIsNotEmpty} from '../../../../utils/validation';
import {ErrorBoundary} from '../../../Reusable/ErrorBoundary/ErrorBoundary';
import Icon from '../../../Reusable/Icon/Icon';
import Comment from '../../Comments/Comment';
import CommentsForm, {CommentsFormData} from '../../Comments/CommentsForm';
import IndividualReviewListItem from '../../Reviews/IndividualReviewListItem/IndividualReviewListItem';


export interface UserSubmittedPhotosModalProps {
  comments?: CommentObject[];
  buttonId: number;
  replyIsClosed: boolean;
  onClickReturn: (updatedCommentId: number | undefined) => void;
  onSubmit: (data: CommentsFormData) => void;
  commentReplies?: (JSX.Element | CommentObject)[];
  updateCommentReplies: (commentId: number) => void;
  photoDataIndex: number;
  handleSwiperSlideChange: (direction: number) => void;
  photoDataList: (CommentPhotoData | ReviewPhotoData)[];
  locale: Locale;
  userId?: number;
  userType?: UserType;
  getListOfRepliesStatus: (commentsChildren: []) => void;
  deleteComment: (commentId: number | undefined) => void;
  viewport: ViewportType;
  assignId: (commentId: number) => void;
  designId?: number;
}

const UserSubmittedPhotosModal = ({
  photoDataIndex, photoDataList, locale, userId, userType, deleteComment, assignId, comments, buttonId, replyIsClosed,
  onClickReturn, getListOfRepliesStatus, commentReplies, handleSwiperSlideChange, updateCommentReplies,
  viewport, designId, onSubmit
}: UserSubmittedPhotosModalProps): JSX.Element => {
  useEffect(() => {
    updateCommentReplies(photoDataList[photoDataIndex].id);
  }, [comments]);

  const isCommentPhotoData = (input: CommentPhotoData | ReviewPhotoData): input is CommentPhotoData =>
    (input as CommentPhotoData).comment !== undefined;

  const createComment = () => {
    const photoData = photoDataList[photoDataIndex] as CommentPhotoData;
    const timeElapsedSinceCreation = getTimeElapsedSinceCreation(locale, new Date(photoData.createdAt));
    const userOwnsComment = photoData.userId === userId;

    return <Comment
      imageSource={photoData.imageSource}
      userName={photoData.userName}
      deletedComment={photoData.deletedComment}
      createdAt={photoData.createdAt}
      timeElapsedSinceCreation={timeElapsedSinceCreation}
      usersComment={photoData.comment}
      userId={userId}
      userType={userType}
      parentId={photoData.parentId}
      commentId={photoData.id}
      userOwnsComment={userOwnsComment}
      deleteComment={deleteComment}
      assignId={assignId} />;
  };

  const createReview = () => {
    const photoData = photoDataList[photoDataIndex] as ReviewPhotoData;

    return isNotUndefined(photoData.review) && <IndividualReviewListItem
      designId={designId}
      userType={userType}
      locale={locale}
      key={photoData.id}
      review={photoData.review}
      reviewTitle={photoData.title}
      reviewText={photoData.text}
      isPhotoModal={isNotUndefined(photoData.photo)}
      openPhotoModal={false}
    />;
  };

  const photoData = photoDataList[photoDataIndex];
  const commentId = photoData.id;
  const photoBelongsToReview = !isCommentPhotoData(photoData) && isNotUndefined(photoData.review);
  const commentReplyList = isCommentPhotoData(photoData) && photoData.commentsReplies;

  const content = photoBelongsToReview ? createReview() : createComment();
  const swiperParams = {
    initialSlide: photoDataIndex,
    slidesPerView: 'auto',
    centeredSlidesBounds: true
  };

  return (
    <ErrorBoundary>
      <div className='b-user-submitted-photos-modal'>
        <h3 className='h3 photo-modal-heading'>
          {photoBelongsToReview ? translate('fabric.userSubmittedPhotos.reviewPhotoModalHeading') :
            translate('fabric.userSubmittedPhotos.commentPhotoModalHeading')}
        </h3>
        <SwiperWrapperContainer
          mountTimer={true}
          viewport={viewport}
          swiperParams={swiperParams}
          handleSwiperSlideChange={handleSwiperSlideChange}
          navigationClasses='x-on-top-of-slides'>
          {photoDataList.map((design, index) => (
            <img
              key={index}
              className='user-submitted-photo swiper-lazy'
              src={design.photo}
              alt={design.caption}
              width='100'
              height='100' />
          ))}
        </SwiperWrapperContainer>
        {content}
        {isCommentPhotoData(photoData) && isNotUndefined(photoData.comment) && (commentId === buttonId && !replyIsClosed) && <CommentsForm
          onSubmit={onSubmit}
          onClickReturn={onClickReturn}
          commentId={commentId}
          isReply={true}
          form={`comment-${commentId}-replies`}
          comments={comments} />}
        {objectIsNotEmpty(commentReplies) && commentReplyList && isNotUndefined(getListOfRepliesStatus(commentReplyList)) &&
          <div className='replies-wrapper'>
            <Icon iconName='arrow-return' iconTitle={translate('designs.comments.reply')} />
            <ul className='replies-list'>{commentReplies}</ul>
          </div>}
      </div>
    </ErrorBoundary>
  );
};

export default UserSubmittedPhotosModal;
